import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';

import type { CreateRecordProperties, PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { copyObject } from 'webapp/utils/common';

import BlockPopupCreateComponent from 'webapp/components/block/popup-create';
import type { Args } from 'webapp/components/popup/inner/modal';

import type BlockRecord from 'webapp/models/block';

// does not currently support predefined blocks
export default class BlockPopupCreateFromMetricSetupComponent extends BlockPopupCreateComponent {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.selectedSourceId = String(this.metricSetup.spaceAccessId);
	}

	override get popupData() {
		return this.args.model.data as PopupData.Block.CreateFromMetricSetup;
	}

	get metricSetup() {
		return this.popupData.metricSetup;
	}

	get metric() {
		return this.metricSetup.metric;
	}

	get datablockName() {
		return this.metricSetup.metric?.name || 'Datablock';
	}

	get canChangeSource() {
		return !!this.metric?.isGeneric;
	}

	get showBlockTypePicker() {
		return true;
	}

	get isAttributed() {
		return this.metricSetup.isOutputMultipleAttributes;
	}

	@action
	override changeSource(sourceId: string) {
		this.metricSetup.spaceAccessId = Number(sourceId);
		super.changeSource(sourceId);
	}

	createDatablock(boardId: string) {
		const layer = this.infolayerService.loading('Creating datablock...'),
			board = this.mmBoardsService.findModel(boardId);

		if (!board) {
			return;
		}

		// this method has quite some overlapping logic from model-manager/blocks::addBlockToBoardInDesigner
		// when c20 is the de-facto block everywhere, join these methods / logics into one

		const metricSetup = this.metricSetup;

		const params: CreateRecordProperties<BlockRecord> = {
			boardId: Number(boardId), // the number attr serializes it with Number() anyway
			name: metricSetup.selectedMetricTitle,
			visualization: this.blockType,
			fromMetricSetup: metricSetup.metricSetupData ?? undefined, // the serializer skips both
			order: board.blocks.length, // put it at the end
			// don't set datawall position so it can take up the space when opened
		};

		if (this.popupData.dateRange) {
			params.fromMetricDateRange = copyObject(this.popupData.dateRange);
		}

		if (
			!params.fromMetricDateRange &&
			!this.mmBlocksService.isVisualizationSeries(params.visualization!)
		) {
			// granulation only, the range comes from the metric (BE reads an empty range as none)
			params.fromMetricDateRange = {
				range: {},
				granulation: { isWholeRange: true },
			};
		}

		const block = this.mmBlocksService.createNewModel(params);
		// tracking
		let trackEvent, trackData;

		if (this.trackOrigin === 'metric') {
			trackEvent = TrackingEvent.METRIC_ADDED_TO_BOARD;
			trackData = {
				origin: 'metrics',
			};
		} else {
			const metric = this.metric;
			if (metric) {
				trackEvent = TrackingEvent.DATABLOCK_DROP;
				trackData = {
					page: this.trackOrigin,
					origin: 'metrickey',
					type: this.metric.isTypeCustomQuery
						? 'query'
						: this.metric.isTypeMetricCalculation
							? 'script'
							: 'basic',
					datasource: this.metric.dataSourceTypeKey,
					metrics: [this.metric.name],
				};
			}
		}

		if (trackEvent) {
			this.analyticsService.event(trackEvent, trackData);
		}

		this.submitInProgress = true;

		return block
			.save()
			.then(() => {
				layer.ok('Datablock created!');
				this.popupData.onBlockCreated?.(board.name, board.id);

				// The BE created the main dataSelect and its compare for us, but a chart also needs the
				// compare LINE — the secondary dataSelect the designer adds on the Number → chart switch
				// and on "Add metric". Without it the chart draws no "Previous period" series and the
				// "Compare" additional line reads as unselected, even though the compare exists.
				// Same call as model-manager/blocks::addBlockToBoardInDesigner; a no-op for non-charts.
				const firstDs = block.blockSetup.firstShownDataSelect;
				if (firstDs) {
					block.blockSetup.checkToAddAdditionalLines(firstDs);
				}

				next(() => {
					this.close();
					this.confirmRedirect(block);
				});
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
}
